/**
 * Sticky identity band for the provider workspace — the passport pattern
 * shared with the org page (GDN-2566): name + membership status, copyable
 * ID/contact chips, and a prominent org line (the hop up the global → org →
 * provider hierarchy) with the roles held in that org.
 */

import { useState } from 'react';
import { Business, Delete, Edit, KeyboardArrowDown, Person } from '@mui/icons-material';
import { Button, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { Chip } from '@jotpsych/ui';

import type { AdminProviderLookupUser } from '@store/generated/admin';

import { IdChip } from './org-page-redesign/primitives';
import { RoleChips } from './role-chips';

interface ProviderPassportProps {
  provider: AdminProviderLookupUser;
  onChangeEmail: () => void;
  onEditPhone: () => void;
  onDeletePhone: () => void;
  onOpenOrganization: (organizationId: string) => void;
}

export function ProviderPassport({
  provider,
  onChangeEmail,
  onEditPhone,
  onDeletePhone,
  onOpenOrganization,
}: ProviderPassportProps) {
  const email = provider.user_info?.email;
  const phone = provider.user_info?.mobile_phone_num;
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  return (
    <div className="sticky top-0 z-10 -mx-4 border-b border-gray-200 bg-white/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
      {/* Title row: identity + actions. IDs live on their own row below so
          nothing wraps raggedly between the two. */}
      <div className="flex flex-wrap items-center gap-2">
        <Person className="shrink-0 text-gray-400" fontSize="small" />
        <Typography variant="h6" noWrap sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {provider.name}
        </Typography>
        {provider.membership_status && (
          <Chip
            label={provider.membership_status}
            variant="soft"
            size="xSmall"
            color={provider.membership_status === 'active' ? 'success' : 'error'}
          />
        )}

        <div className="ml-auto flex items-center gap-3">
          <Button
            size="small"
            variant="outlined"
            endIcon={<KeyboardArrowDown />}
            onClick={e => setMenuAnchor(e.currentTarget)}
          >
            Actions
          </Button>
          <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onChangeEmail();
              }}
            >
              Change login email
            </MenuItem>
            {!phone && (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  onEditPhone();
                }}
              >
                Add phone number
              </MenuItem>
            )}
          </Menu>
        </div>
      </div>

      {/* Org context — the provider's place in the global → org → provider
          hierarchy: a prominent hop to the org, plus the roles held there. */}
      {provider.organization_id && (
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => onOpenOrganization(provider.organization_id!)}
            className="flex items-center gap-1.5 text-sm font-semibold text-indigo-700 hover:underline"
          >
            <Business sx={{ fontSize: 16 }} />
            {provider.organization_name || 'Organization'}
          </button>
          <RoleChips roles={provider.roles} emptyLabel="No roles in this organization" />
        </div>
      )}

      {/* Dedicated IDs row — the reference material admins copy, in one
          predictable slot. */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <IdChip label="id" value={provider.provider_id} />
        {provider.organization_id && <IdChip label="org" value={provider.organization_id} />}
        {email && <IdChip label="email" value={email} truncate={false} />}
        {provider.dosespot_clinician_id && (
          <IdChip label="rx clinician" value={provider.dosespot_clinician_id} />
        )}
      </div>

      {/* Contact row — editable, unlike the copy-only IDs above. */}
      {phone && (
        <div className="mt-1.5 flex items-center gap-0.5 text-xs">
          <span className="font-medium uppercase tracking-wide text-gray-400">tel</span>
          <span className="ml-1 font-mono text-gray-700">{phone}</span>
          <Tooltip title="Edit phone number">
            <IconButton size="small" aria-label="Edit phone number" onClick={onEditPhone}>
              <Edit sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove phone number">
            <IconButton size="small" aria-label="Remove phone number" onClick={onDeletePhone}>
              <Delete sx={{ fontSize: 14, color: 'error.main' }} />
            </IconButton>
          </Tooltip>
        </div>
      )}
    </div>
  );
}
